import type { TranslateFunction } from '@locales/generated';
import { sortedBy } from '@shared/utils/sort';
import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { mapToLabel, styleFacetOptionForConceptName } from './components/Filters/facetOptions';
import type { Facet, FilterFacet } from './components/Filters/Filter.types';
import { FACET_ORDER, STYLE_FACET } from './components/Filters/Filters.constants';
import { useIsPublicGallerySsr } from './useIsPublicGallerySsr';
import type { StationeryTemplate } from './DesignsGallery.types';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { usePrecomputedEmbeddings } from '@shared/utils/search';

export const useFilterFacets = (templates: StationeryTemplate[], colorwayColorHits: ReadonlyArray<ColorwayColorHit>) => {
  const { t, tq } = useTranslation('stationery');
  const { themes } = usePrecomputedEmbeddings();
  const isPublicGallerySsr = useIsPublicGallerySsr();
  const tFilterOptions = t('dashboard', 'designsGallery', 'filterOptions') as Record<string, TranslateFunction>;

  // The size facet's labels come from tq — unlike the `filterOptions` map above,
  // whose locale keys are externally maintained and can't take additions from this
  // change. These tq calls must sit lexically inside this hook (the translate
  // extractor cannot trace tq through function parameters). The postcard label is
  // the blessed product name from `useCardSizeLabels`; the classic 5x7 has no
  // blessed name, so it reads as a measurement.
  const { postcard4x6: sizePostcardLabel } = useCardSizeLabels();
  const sizeFacetLabel = tq`Size`;
  const sizeClassicLabel = tq`5 × 7`;
  // `product` facet labels follow the same code-owned pattern as `size`.
  const productFacetLabel = tq`Product`;
  const productMagnetLabel = tq`Magnet`;

  return useMemo(() => {
    const localFacetLabels: Record<string, string> = {
      size: sizeFacetLabel,
      'size classic': sizeClassicLabel,
      'size postcard': sizePostcardLabel,
      product: productFacetLabel,
      'product magnet': productMagnetLabel,
    };

    // Tracked per option as a set of template ids rather than a running count, so
    // a template matching a color by both its tags and a colorway counts once.
    const facetOptionTemplateIds = new Map<string, Set<string>>();
    const addFacetOptionMatch = (option: string, templateId: string) => {
      const ids = facetOptionTemplateIds.get(option) ?? new Set<string>();
      ids.add(templateId);
      facetOptionTemplateIds.set(option, ids);
    };

    for (const template of templates) {
      for (const option of template.mappedTags) {
        // Mapped tags remain the source for non-style facets only. Style choices
        // come exclusively from precomputed, color-neutral theme embeddings.
        if (!option.startsWith(`${STYLE_FACET}:`)) addFacetOptionMatch(option, template.id);
      }

      // Styles intentionally come only from the base theme row. Embeddings are
      // color-neutral and style tags generally do not vary by color, so do not
      // reintroduce colorway traversal here; it belongs only to COLOR matching.
      if (themes) {
        for (const conceptName of themes.get(template.themeId)?.conceptNames ?? []) {
          const option = styleFacetOptionForConceptName(conceptName);
          if (option) addFacetOptionMatch(option, template.id);
        }
      }
    }
    // Keeps color counts in step with the filter, which also matches per colorway.
    for (const hit of colorwayColorHits) addFacetOptionMatch(hit.colorOption, hit.templateId);

    const facetOptionCounts = new Map([...facetOptionTemplateIds].map(([option, ids]) => [option, ids.size]));

    const facets = new Map<Facet, typeof facetOptionCounts>();
    for (const [facetOption, count] of Array.from(facetOptionCounts.entries())) {
      const facet = facetOption.split(':')[0] as Facet;
      facets.set(facet, (facets.get(facet) ?? new Map()).set(facetOption, count));
    }

    const facetsForUI: FilterFacet[] = Array.from(facets.entries()).map(([facet, options]) => {
      return {
        groupCode: facet,
        label: tFilterOptions[facet]?.() ?? localFacetLabels[facet] ?? `[${facet}]`,
        options: Array.from(options.entries()).map(([option, count]) => {
          const translationKey = option.replace(':', ' '); // Don't use colon as separator, i18next will think it's a namespace
          return {
            compositeCode: option,
            label: tFilterOptions[translationKey]?.() ?? localFacetLabels[translationKey] ?? mapToLabel(option) ?? `[${translationKey}]`,
            matchingItemCount: count,
          };
        }),
      };
    });

    for (const facet of facetsForUI) {
      facet.options.sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
    }
    // Style is counted from the embeddings index, which only the client loads. On the
    // server-rendered public gallery (PRNT-5494) a placeholder pill holds its place on both
    // renders — the same row on the server and at hydration — and fills in once the index lands,
    // instead of appearing first in the row and shoving every other pill along.
    if (isPublicGallerySsr && !themes && !facets.has(STYLE_FACET)) {
      facetsForUI.push({ groupCode: STYLE_FACET, label: tFilterOptions[STYLE_FACET]?.() ?? `[${STYLE_FACET}]`, options: [], pending: true });
    }

    return sortedBy(facetsForUI, f => f.groupCode, FACET_ORDER);
  }, [tFilterOptions, templates, colorwayColorHits, themes, isPublicGallerySsr, sizeFacetLabel, sizeClassicLabel, sizePostcardLabel, productFacetLabel, productMagnetLabel]);
};
